import React, { useState, useEffect } from 'react';
import PageLoader from '../components/PageLoader';
import FeedbackModal from '../components/FeedbackModal';
import { api } from '../App';
import '../styles/AdminProductenPage.css';
import '../styles/AdminGebruikersbeheerPage.css';

function emptyForm() {
  return {
    inlognaam: '',
    wachtwoord: '',
    contact_persoon: '',
    email: '',
    telefoon: ''
  };
}

function AdminGebruikersbeheerPage({ onBack, onLogout }) {
  const [gebruikers, setGebruikers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadGebruikers();
  }, []);

  const loadGebruikers = async () => {
    try {
      setError('');
      const response = await api.get('/index.php?action=admingebruikers');
      setGebruikers(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij laden van gebruikers');
      setLoading(false);
    }
  };

  const startEdit = (gebruiker) => {
    setEditingId(gebruiker.user_id);
    setShowPassword(false);
    setForm({
      inlognaam: gebruiker.inlognaam || '',
      wachtwoord: '',
      contact_persoon: gebruiker.contact_persoon || '',
      email: gebruiker.email || '',
      telefoon: gebruiker.telefoon || ''
    });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowPassword(false);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingId) return;

    try {
      setSaving(true);
      setError('');
      await api.put('/index.php?action=admingebruiker', {
        user_id: editingId,
        inlognaam: form.inlognaam.trim(),
        wachtwoord: form.wachtwoord,
        contact_persoon: form.contact_persoon.trim(),
        email: form.email.trim(),
        telefoon: form.telefoon.trim()
      });
      setSuccess('Gegevens opgeslagen');
      cancelEdit();
      loadGebruikers();
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="admin-producten-container admin-gebruikers-container">
      {success && (
        <FeedbackModal
          title="Gelukt"
          message={success}
          onConfirm={() => setSuccess('')}
          confirmLabel="Ok"
        />
      )}

      <header className="admin-header">
        <button type="button" onClick={onBack} className="back-button">← Terug</button>
        <h1>Gebruikersbeheer</h1>
        <button type="button" onClick={onLogout} className="logout-button">Uitloggen</button>
      </header>

      <main className="admin-main">
        <div className="admin-content admin-gebruikers-content">
          {error && <div className="error-message">{error}</div>}

          <p className="info-text admin-gebruikers-intro">
            Beheer inloggegevens van speltakken en vul contactgegevens aan. Laat het
            wachtwoord leeg om het huidige wachtwoord te behouden.
          </p>

          <div className="gebruikers-list">
            {gebruikers.length === 0 ? (
              <p className="gebruikers-empty">Geen speltak-accounts gevonden.</p>
            ) : (
              gebruikers.map((gebruiker) => {
                const isEditing = editingId === gebruiker.user_id;

                return (
                  <article key={gebruiker.user_id} className="gebruiker-card">
                    <div className="gebruiker-card-header">
                      <div>
                        <h2>{gebruiker.speltak_naam || 'Speltak'}</h2>
                        <p className="gebruiker-meta">
                          Inlognaam: <strong>{gebruiker.inlognaam}</strong>
                        </p>
                      </div>
                      {!isEditing && (
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => startEdit(gebruiker)}
                        >
                          Bewerken
                        </button>
                      )}
                    </div>

                    {!isEditing ? (
                      <dl className="gebruiker-details">
                        <div>
                          <dt>Contactpersoon</dt>
                          <dd>{gebruiker.contact_persoon || '—'}</dd>
                        </div>
                        <div>
                          <dt>E-mail</dt>
                          <dd>{gebruiker.email || '—'}</dd>
                        </div>
                        <div>
                          <dt>Telefoon</dt>
                          <dd>{gebruiker.telefoon || '—'}</dd>
                        </div>
                      </dl>
                    ) : (
                      <form className="gebruiker-form" onSubmit={handleSave}>
                        <div className="form-grid">
                          <label>
                            Inlognaam
                            <input
                              type="text"
                              value={form.inlognaam}
                              onChange={(e) => handleFieldChange('inlognaam', e.target.value)}
                              required
                              disabled={saving}
                            />
                          </label>

                          <label>
                            Nieuw wachtwoord
                            <span className="field-hint">(optioneel)</span>
                            <div className="password-input-wrap">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={form.wachtwoord}
                                onChange={(e) => handleFieldChange('wachtwoord', e.target.value)}
                                placeholder="Laat leeg om niet te wijzigen"
                                disabled={saving}
                                autoComplete="new-password"
                              />
                              <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword((v) => !v)}
                                disabled={saving}
                                aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                              >
                                {showPassword ? '🙈' : '👁'}
                              </button>
                            </div>
                          </label>

                          <label>
                            Contactpersoon
                            <input
                              type="text"
                              value={form.contact_persoon}
                              onChange={(e) => handleFieldChange('contact_persoon', e.target.value)}
                              disabled={saving}
                            />
                          </label>

                          <label>
                            E-mail
                            <input
                              type="email"
                              value={form.email}
                              onChange={(e) => handleFieldChange('email', e.target.value)}
                              disabled={saving}
                            />
                          </label>

                          <label>
                            Telefoon
                            <input
                              type="tel"
                              value={form.telefoon}
                              onChange={(e) => handleFieldChange('telefoon', e.target.value)}
                              maxLength={20}
                              disabled={saving}
                            />
                          </label>
                        </div>

                        <div className="form-actions">
                          <button type="button" className="secondary-button" onClick={cancelEdit} disabled={saving}>
                            Annuleren
                          </button>
                          <button type="submit" className="primary-button" disabled={saving}>
                            {saving ? 'Opslaan...' : 'Opslaan'}
                          </button>
                        </div>
                      </form>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminGebruikersbeheerPage;
